import React, { useEffect, useState } from 'react';
import { List } from '@meitu/saas-ui';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { MaterialCard } from '@meitu/saas-editor-ui/es/components/material-card';
import { WheelCard } from '@meitu/saas-editor-ui/es/components/wheel-card';
import { formatImageUrl } from '@meitu/saas-editor-ui/es/utils/file-kit';
import IconFont from '@meitu/xdesign-icon-font';
import { useDragLayer } from '@/hooks/use-drag-layer';
import { getLocaleStore, useLocaleStore } from '@/i18n';
import { fetchMaterialTopicList, fetchNewMaterialList } from '@/services/material';
import { MaterialListItem, MaterialTopicItem } from '@/services/material/index.d';
import { checkShowSvipCorner, isInRecommendMaterialTest } from '@/utils/ab-test';
import { useSelectMaterial } from '../../hooks/use-select-material';

const MAX_PAGE_COUNT = 6;

interface IMaterialListCollapse {
    onShowDetailsCollapse: Function;
    style?: React.CSSProperties;
    topView?: () => JSX.Element;
}

/**
 * 请求最新素材
 * @returns
 */
const queryLatestTopic = async () => {
    const { $t } = getLocaleStore();
    const { data: res } = await fetchNewMaterialList({
        count: 6,
    });

    const { data } = res;

    const materials = data?.materials || [];
    if (materials.length === 0) return null;

    return {
        id: 'newMaterial',
        materials: materials as any,
        name: $t((v) => v.collapse.materialCollapse.newestTitle),
        cursor: '',
    };
};

/**
 * 请求专题素材
 * @param cursor
 * @returns
 */
const queryTopics = async (cursor = '') => {
    const isInTest = isInRecommendMaterialTest();
    const params = isInTest ? { is_rec: 1 } : {};
    const { data: res } = await fetchMaterialTopicList({
        material_count: MAX_PAGE_COUNT,
        cursor,
        ...params,
    });

    const { response: data } = res;
    const topics = data?.data || [];

    return { topics, topicsCursor: data?.cursor || '' };
};

const AllMaterialList = (props: IMaterialListCollapse) => {
    const { $t } = useLocaleStore();
    const { onShowDetailsCollapse, topView: TopView } = props;
    const [materialList, setMaterialList] = useState<MaterialTopicItem[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [isInit, setIsInit] = useState<boolean>(false);
    const dragLayerHelper = useDragLayer();
    const [cursor, setCursor] = useState<string>('');
    const selectMaterial = useSelectMaterial();

    /**
     * 请求专题列表
     */
    const getMaterialTopicList = async () => {
        if (!hasMore || loading) {
            return;
        }
        setLoading(true);
        const { topics, topicsCursor } = await queryTopics(cursor);
        topicsCursor ? setCursor(topicsCursor) : setHasMore(false);
        topics.length && setMaterialList((v) => v.concat(topics));
        setLoading(false);
    };

    /**
     * 请求所有数据
     */
    const init = async () => {
        try {
            setIsInit(true);
            setLoading(true);
            const isInTest = isInRecommendMaterialTest();
            // 请求最新素材和专题列表
            const [latestTopic, { topics, topicsCursor }] = await Promise.all(
                isInTest ? [null, queryTopics()] : [queryLatestTopic(), queryTopics()],
            );

            topicsCursor ? setCursor(topicsCursor) : setHasMore(false);
            const list: MaterialTopicItem[] = [];

            // 先添加最新素材
            latestTopic && list.push(latestTopic);
            // 再添加专题列表
            topics.length && list.push(...topics);
            setMaterialList(list);
        } catch (error) {
            console.log('请求素材失败');
        } finally {
            setIsInit(false);
            setLoading(false);
        }
    };

    const handleClick = (name: string, id: number | string) => {
        onShowDetailsCollapse(true, name, id);
    };

    const handleSelectMaterial = (item: MaterialListItem, byMousePosition = false) => {
        selectMaterial.append(item, byMousePosition);
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <div className="all-material-list" style={props.style}>
            <List<MaterialTopicItem>
                className="template-list"
                id="allMaterialList"
                loading={loading}
                hasMore={hasMore}
                dataSource={isInit ? [] : materialList}
                nextPageRequest={getMaterialTopicList}
                preContent={TopView && <TopView />}
                renderItem={(data) => (
                    <WheelCard
                        cardWidth={78}
                        cardMargin={6}
                        cardLength={data.materials.length}
                        key={data.id}
                        title={data.name}
                        rightText={
                            data.materials.length >= 6 ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {$t((v) => v.collapse.materialCollapse.all)}
                                    <IconFont name="ChevronRightBlack" fontSize={12} />
                                </div>
                            ) : (
                                ''
                            )
                        }
                        onClick={() => handleClick(data.name || '', data.id)}
                    >
                        {data.materials.map((material, materialIndex) => {
                            return (
                                <div
                                    key={materialIndex}
                                    draggable="true"
                                    onMouseDown={(ev) => {
                                        dragLayerHelper.init(
                                            {
                                                naturalWidth: (material as MaterialListItem).material_conf?.width,
                                                naturalHeight: (material as MaterialListItem).material_conf?.height,
                                                callback: () =>
                                                    handleSelectMaterial(material as MaterialListItem, true),
                                            },
                                            ev,
                                        );
                                    }}
                                    onMouseUp={() => {
                                        dragLayerHelper.destroy();
                                    }}
                                >
                                    <MaterialCard
                                        onClick={() => handleSelectMaterial(material as MaterialListItem)}
                                        width={78}
                                        key={materialIndex}
                                        src={imageConverter(formatImageUrl(material?.thumbnail), 320)}
                                        cornerSrc={
                                            checkShowSvipCorner()
                                                ? typeof material.corner_mark === 'string'
                                                    ? material.corner_mark
                                                    : ((material.corner_mark as any)?.url ?? '')
                                                : ''
                                        }
                                        cornerPlacement="topRight"
                                        cardStyle={{
                                            marginRight: 6,
                                            height: 78,
                                        }}
                                    ></MaterialCard>
                                </div>
                            );
                        })}
                    </WheelCard>
                )}
            />
        </div>
    );
};

export default React.memo(AllMaterialList);
